import { useState, useEffect, useRef } from 'react';
import { RealtimeParkingViewModel } from '../../../viewmodels/RealtimeParkingViewModel';
import { CctvTemplate } from '../../../models/CctvTemplate';
import { ModalImageData } from '../types';

interface UseRealtimeImageRefreshParams {
  projectId: string;
  templateBasedMode: boolean;
  selectedCctv: string;
  cctvTemplate: CctvTemplate | null;
  isRunning: boolean;
  selectedCctvImages: any;
  setSelectedCctvImages: (images: any) => void;
  setCctvImageError: (error: string | null) => void;
  setImageUpdateKey: (fn: (prev: number) => number) => void;
  modalImage: ModalImageData | null;
  /** false 면 주기 폴링을 멈춘다 — 수동 새로고침 버튼만 쓰는 모드. */
  autoRefresh?: boolean;
  setModalImage: React.Dispatch<React.SetStateAction<ModalImageData | null>>;
}

export function useRealtimeImageRefresh({
  projectId,
  templateBasedMode,
  selectedCctv,
  cctvTemplate,
  isRunning,
  selectedCctvImages,
  setSelectedCctvImages,
  setCctvImageError,
  setImageUpdateKey,
  modalImage,
  setModalImage,
  autoRefresh = true,
}: UseRealtimeImageRefreshParams) {
  const [, setRemainingTime] = useState(60);
  const [lastLearningTime] = useState<number>(0);
  const imageRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Template mode: auto-refresh every 60s
  useEffect(() => {
    if (autoRefresh && templateBasedMode && selectedCctv && cctvTemplate) {
      const refreshImages = () => {
        try {
          const images = RealtimeParkingViewModel.getTemplateBasedCctvImages(projectId, selectedCctv, cctvTemplate);
          setSelectedCctvImages(images);
          setImageUpdateKey(prev => prev + 1);
          setCctvImageError(null);
          setRemainingTime(60);
          console.log('\uC774\uBBF8\uC9C0 \uC0C8\uB85C\uACE0\uCE68:', selectedCctv);
        } catch (error: any) {
          console.error('\uC774\uBBF8\uC9C0 \uC0C8\uB85C\uACE0\uCE68 \uC2E4\uD328:', error);
          setCctvImageError(error.message || 'CCTV \uC774\uBBF8\uC9C0 \uB85C\uB4DC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
        }
      };

      imageRefreshIntervalRef.current = setInterval(refreshImages, 60000);

      const countdownInterval = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            return 60;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (imageRefreshIntervalRef.current) {
          clearInterval(imageRefreshIntervalRef.current);
        }
        clearInterval(countdownInterval);
      };
    }
  }, [autoRefresh, templateBasedMode, selectedCctv, cctvTemplate, projectId, setSelectedCctvImages, setCctvImageError, setImageUpdateKey]);

  // API-based mode: update images when selectedCctv changes
  useEffect(() => {
    if (selectedCctv && isRunning && !templateBasedMode) {
      const updateCctvImages = async () => {
        try {
          const images = await RealtimeParkingViewModel.getRealtimeCctvImages(projectId, selectedCctv);
          setSelectedCctvImages(images);
          setImageUpdateKey(prev => prev + 1);
          setCctvImageError(null);
        } catch (error: any) {
          console.error('CCTV \uC774\uBBF8\uC9C0 \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328:', error);
          setCctvImageError(error.message || 'CCTV \uC774\uBBF8\uC9C0 \uC5C5\uB370\uC774\uD2B8 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
        }
      };

      updateCctvImages();
    }
  }, [selectedCctv, isRunning, templateBasedMode, projectId, setSelectedCctvImages, setCctvImageError, setImageUpdateKey]);

  // Update images after learning completion
  useEffect(() => {
    if (selectedCctv && isRunning && lastLearningTime > 0) {
      const updateCctvImages = async () => {
        try {
          const images = await RealtimeParkingViewModel.getRealtimeCctvImages(projectId, selectedCctv);
          setSelectedCctvImages(images);
          setImageUpdateKey(prev => prev + 1);
          setCctvImageError(null);
        } catch (error: any) {
          console.error('\uC2E4\uC2DC\uAC04 \uD559\uC2B5 \uD6C4 CCTV \uC774\uBBF8\uC9C0 \uC5C5\uB370\uC774\uD2B8 \uC2E4\uD328:', error);
          setCctvImageError(error.message || 'CCTV \uC774\uBBF8\uC9C0 \uC5C5\uB370\uC774\uD2B8 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
        }
      };

      updateCctvImages();
    }
  }, [lastLearningTime, selectedCctv, isRunning, projectId, setSelectedCctvImages, setCctvImageError, setImageUpdateKey]);

  // Modal image real-time update
  useEffect(() => {
    if (modalImage && selectedCctvImages) {
      if (modalImage.title.includes('ROI Result')) {
        setModalImage(prev => prev ? {
          ...prev,
          src: selectedCctvImages.roiResultImage
        } : null);
      } else if (modalImage.title.includes('Foreground Mask')) {
        setModalImage(prev => prev ? {
          ...prev,
          src: selectedCctvImages.fgMaskImage
        } : null);
      }
    }
  }, [selectedCctvImages, modalImage, setModalImage]);

  return {
    imageRefreshIntervalRef,
    setRemainingTime,
  };
}
